import { apiClient } from '../../apiClient';
import { apiUrl } from '../../apiUrls';

export type ChatMetadata = {
    id: string;
    name: string;
}

type ChatMetadatasResponse = {
    chatMetadatas: ChatMetadata[];
    count: number;
    next: string | null;
    previous: string | null;
}

export async function getChatMetadatasForProject(projectId: string, url?: string): Promise<ChatMetadatasResponse> {
    if (url === undefined) {
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- backend REST endpoint path (infrastructure id), not user copy.
        url = `/api/projects/${projectId}/chats/`;
    }

    const resolvedUrl = url.startsWith('http') ? url : apiUrl(url);
    const response = await apiClient.get(resolvedUrl);
    return {
        chatMetadatas: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
    }
}

type ProjectChatMetadataMap = Record<string, ChatMetadatasResponse>;

export async function batchGetChatMetadatasForProjects(projectIds: string[]): Promise<ProjectChatMetadataMap> {
    const projectChatMetadataList = await Promise.all(
        projectIds.map(async projectId => {
            const response = await getChatMetadatasForProject(projectId);
            return { projectId, response }
        })
    );

    return projectChatMetadataList.reduce<ProjectChatMetadataMap>(
        (acc, { projectId, response }) => {
            acc[projectId] = response;
            return acc;
        }, 
        {}
    );
}

export async function createChat(
    projectId: string | null,
    name: string | null,
    first_message: string = ""): Promise<ChatMetadata> {

    let url: string;
    let body: { name?: string; first_message?: string };

    if (projectId) {
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- backend REST endpoint path (infrastructure id), not user copy.
        url = apiUrl(`/api/projects/${projectId}/chats/`);
        if (first_message.trim()) {
            body = { first_message: first_message.trim() };
        } else {
            body = { name: name?.trim() || 'New chat' };
        }
    } else {
        // Create projectless chat via /api/chats/
        url = apiUrl('/api/chats/');
        if (first_message.trim()) {
            body = { first_message: first_message.trim() };
        } else {
            body = { name: name?.trim() || 'New chat' };
        }
    }

    const response = await apiClient.post(url, body);
    return response.data;
}

export async function getProjectlessChats(url?: string): Promise<ChatMetadatasResponse> {
    const resolvedUrl = url
        ? (url.startsWith('http') ? url : apiUrl(url))
        : apiUrl('/api/chats/?has_project=false');

    const response = await apiClient.get(resolvedUrl);
    return {
        chatMetadatas: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
    };
}

// ── Global search ──────────────────────────────────────────────────

export type SearchResultChat = {
    id: string;
    name: string;
    /** `'project'` when the row is a project header, `'chat'` when it's a
     *  message thread. Server-derived; consumers use it to route clicks. */
    type: 'project' | 'chat';
    /** Parent project id (string uuid) — null for project rows or
     *  project-less chats. Named `project` on the client for brevity;
     *  backend writes `project_id`, unpacked below. */
    project?: string | undefined;
    project_name?: string | undefined;
};

export type SearchResultsPage = {
    results: SearchResultChat[];
    count: number;
    next: string | null;
};

/** Coerce an incoming row into the client shape. Backend ships a couple
 *  of legacy field names (`project_id` vs `project`) — this keeps the
 *  service boundary clean so downstream code never has to guess. */
function normalizeSearchRow(raw: unknown): SearchResultChat {
    const r = (raw ?? {}) as Record<string, unknown>;
    // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator values for SearchResultChat union (infrastructure ids), not user copy.
    const type = r.type === 'project' ? 'project' : 'chat';
    const project =
        typeof r.project === 'string' ? r.project
        : typeof r.project_id === 'string' ? r.project_id
        : undefined;
    const projectName =
        typeof r.project_name === 'string' ? r.project_name
        : undefined;
    return {
        id: String(r.id ?? ''),
        name: String(r.name ?? ''),
        type,
        ...(project !== undefined ? { project } : {}),
        ...(projectName !== undefined ? { project_name: projectName } : {}),
    };
}

/** Searches chats + projects by name. Used by the sidebar search pill.
 *  Returns DRF pagination shape so usePagedList / manual paging can
 *  consume it. */
export async function searchChats(query: string): Promise<SearchResultsPage> {
    const res = await apiClient.get(`/api/search/?q=${encodeURIComponent(query.trim())}`);
    const d = (res.data ?? {}) as { results?: unknown[]; count?: number; next?: string };
    return {
        results: Array.isArray(d.results) ? d.results.map(normalizeSearchRow) : [],
        count: typeof d.count === 'number' ? d.count : 0,
        next: typeof d.next === 'string' ? d.next : null,
    };
}

/** Load the next page from a server-provided search URL. */
export async function searchChatsByUrl(url: string): Promise<SearchResultsPage> {
    const res = await apiClient.get(url);
    const d = (res.data ?? {}) as { results?: unknown[]; count?: number; next?: string };
    return {
        results: Array.isArray(d.results) ? d.results.map(normalizeSearchRow) : [],
        count: typeof d.count === 'number' ? d.count : 0,
        next: typeof d.next === 'string' ? d.next : null,
    };
}
